import React, { useState, useEffect,useRef,useCallback } from 'react';
import IntlMessages from '@iso/components/utility/intlMessages';
import AssetModalWrapper from './AssetModal.styles';
import { Form, Input, Button, Select, Row, Col, DatePicker, TimePicker, Tabs, Card, Timeline, Popconfirm, InputNumber, Checkbox, Radio,Upload,Space, message, Table, Divider } from 'antd';
import { activityPolicy } from '@iso/constants/activityPolicy';
import { CloseOutlined, UploadOutlined, PlusOutlined, DeleteOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { propertyService,healthsafety,attachment,userService,divisionService,divisionSiteService } from '@iso/services';
import { deliveryMethods } from '@iso/constants/deliveryMethods';
import _ from 'lodash';
import L from 'leaflet';
import { dateHelper } from '@iso/lib/helpers/dateHelper';
import moment from 'moment';
import { Map, TileLayer, FeatureGroup, Polygon, Tooltip, ZoomControl,Marker,Popup } from 'react-leaflet';
import { apiEndpoint, apiEndpointFunction } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import TagsInput from 'react-tagsinput'
import 'react-tagsinput/react-tagsinput.css'

const { Option } = Select;

const AddActivityModal = (props) => {
  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef();
  const [valueLat, setValuelat] = useState();
  const [valueLng, setValueLng] = useState();
  const [attachmentActivityId, setAttachmentActivityId] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [propertyUsers, setPropertyUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [sites, setSites] = useState([]);
  const [property, setProperty] = useState(null);
  const [allDivisionSites, setAllDivisionSites] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [traineeCompetencies, setTraineeCompetencies] = useState({});
  const [combinedTrainees, setCombinedTrainees] = useState([]);
  const [traineesLoading, setTraineesLoading] = useState(false);
  const [outsideTrainees, setOutsideTrainees] = useState([]);

  // When the activity targets an education asset, the trainer dropdown is
  // restricted to that asset's specified trainers — primary + secondary
  // registered users, plus sentinel options for the primary and/or secondary
  // external trainer (when present). The session API translates these
  // sentinels to trainer_id = NULL on the wire, and the trainer name is read
  // back from the asset.
  const educationAsset = props.educationAsset || null;
  const primaryExternal = educationAsset && educationAsset.primary_external_trainer
    ? educationAsset.primary_external_trainer
    : null;
  const secondaryExternal = educationAsset
    && Array.isArray(educationAsset.secondary_external_trainers)
    && educationAsset.secondary_external_trainers.length > 0
    ? educationAsset.secondary_external_trainers[0]
    : null;
  const trainerOptions = React.useMemo(() => {
    if (!educationAsset) return users;
    const allowedIds = new Set();
    if (educationAsset.primary_trainer_id) allowedIds.add(educationAsset.primary_trainer_id);
    (educationAsset.secondary_trainer_ids || []).forEach(id => allowedIds.add(id));
    return users.filter(u => allowedIds.has(u.id));
  }, [educationAsset, users]);

  useEffect(() => {
    if (props.visibleActivity && props.propertyId) {
      propertyService.viewProperty(props.propertyId).then(res => {
        if (res && res.property) {
          setProperty(res.property);
        }
      }).catch(err => {
        console.error('Error loading property:', err);
      });

      propertyService.getListUsersOfProperty(props.propertyId).then(res => {
        if (res.code === '0000') {
          setPropertyUsers(res.users || []);
        }
      });

      userService.getUserForProperty(props.propertyId).then(res => {
        if (res && res.code === '0000') {
          setUsers(res.users || []);
        }
      }).catch(err => {
        console.error('Error loading users for trainers:', err);
      });
    }
  }, [props.visibleActivity, props.propertyId]);

  // Load divisions and sites when property is loaded
  useEffect(() => {
    if (property && property.corporate_group_id) {
      // Load divisions from corporate group
      divisionService.getList(property.corporate_group_id, false).then(res => {
        if (res && res.code === '0000') {
          setDivisions(res.divisions || []);

          // Load division sites for all divisions
          const sitePromises = (res.divisions || []).map(division =>
            divisionSiteService.getList(division.id).then(siteRes => {
              if (siteRes && siteRes.code === '0000') {
                return (siteRes.division_sites || []).map(site => ({
                  ...site,
                  division_id: division.id,
                  division_name: division.name
                }));
              }
              return [];
            }).catch(() => [])
          );

          Promise.all(sitePromises).then(sitesArrays => {
            const flattenedSites = sitesArrays.flat();
            setAllDivisionSites(flattenedSites);
          });
        }
      }).catch(err => {
        console.error('Error loading divisions:', err);
      });
    }
  }, [property]);

  // Filter sites based on selected division
  useEffect(() => {
    if (selectedDivision && allDivisionSites.length > 0) {
      const filteredSites = allDivisionSites.filter(site => site.division_id === selectedDivision);
      setSites(filteredSites);
    } else {
      setSites([]);
    }
  }, [selectedDivision, allDivisionSites]);

  // Initialize combinedTrainees with propertyUsers
  useEffect(() => {
    setCombinedTrainees(propertyUsers);
  }, [propertyUsers]);

  // Load trainees from selected sites
  const loadTraineesFromSites = useCallback(async (selectedSiteIds) => {
    if (!selectedSiteIds || selectedSiteIds.length === 0) {
      setCombinedTrainees(propertyUsers);
      return;
    }

    setTraineesLoading(true);
    try {
      // Get property IDs from selected sites
      const selectedSites = allDivisionSites.filter(site =>
        selectedSiteIds.includes(site.id)
      );
      const sitePropertyIds = [...new Set(selectedSites.map(site => site.property_id))];
      // Include current property ID and site property IDs
      const allPropertyIds = [...new Set([props.propertyId, ...sitePropertyIds])];

      const res = await propertyService.getUsersFromMultipleProperties(allPropertyIds);
      if (res.code === '0000') {
        setCombinedTrainees(res.users);
      } else {
        setCombinedTrainees(propertyUsers);
      }
    } catch (error) {
      console.error('Error loading trainees from sites:', error);
      setCombinedTrainees(propertyUsers);
    } finally {
      setTraineesLoading(false);
    }
  }, [allDivisionSites, propertyUsers, props.propertyId]);

  useEffect(() => {
      const initialFields = [
        {
          name: ['asset', 'date_time'],
          value: moment(),
        },
        {
          name: ['asset', 'start_time'],
          value: moment(),
        },
      ];

      // Add default activity type if provided
      if (props.defaultActivityType) {
        initialFields.push({
          name: ['asset', 'type'],
          value: props.defaultActivityType,
        });
      }

      // Auto-populate training_name with asset name for training sessions
      if (props.defaultActivityType === 'training_session' && props.assetName) {
        initialFields.push({
          name: ['asset', 'training_name'],
          value: props.assetName,
        });
      }

      setFields(initialFields);

      // When modal opens, also set form values directly to ensure proper initialization
      if (props.visibleActivity) {
        const formValues = {
          asset: {
            date_time: moment(),
            start_time: moment(),
          }
        };
        if (props.defaultActivityType) {
          formValues.asset.type = props.defaultActivityType;
        }
        // Auto-populate training_name with asset name for training sessions
        if (props.defaultActivityType === 'training_session' && props.assetName) {
          formValues.asset.training_name = props.assetName;
        }
        // Use setTimeout to ensure form is mounted before setting values
        setTimeout(() => {
          form.setFieldsValue(formValues);
        }, 0);
      }
  }, [props.propertyId, props.visibleActivity, props.defaultActivityType, props.assetName]);
  const handleChangeTag = (input_tags) => {
    setAttendees(input_tags);
  }

  // Add outside trainee to list
  const handleAddOutsideTrainee = () => {
    const firstName = form.getFieldValue(['asset', 'outside_trainee_first_name']);
    const lastName = form.getFieldValue(['asset', 'outside_trainee_last_name']);
    const email = form.getFieldValue(['asset', 'outside_trainee_email']);
    const mobile = form.getFieldValue(['asset', 'outside_trainee_mobile']);

    if (!firstName || !lastName || !email) {
      message.warning('Please enter first name, last name and email');
      return;
    }

    const newTrainee = {
      id: `outside_${Date.now()}`,
      first_name: firstName || '',
      last_name: lastName || '',
      email: email || '',
      mobile_phone: mobile || '',
      competency_status: 'competent',
    };

    setOutsideTrainees([...outsideTrainees, newTrainee]);

    // Clear form fields
    form.setFieldsValue({
      asset: {
        ...form.getFieldValue('asset'),
        outside_trainee_first_name: '',
        outside_trainee_last_name: '',
        outside_trainee_email: '',
        outside_trainee_mobile: '',
      }
    });

    message.success('Outside trainee added');
  };

  // Remove outside trainee from list
  const handleRemoveOutsideTrainee = (traineeId) => {
    setOutsideTrainees(outsideTrainees.filter(t => t.id !== traineeId));
  };

  // Update outside trainee competency
  const handleOutsideTraineeCompetencyChange = (traineeId, status) => {
    setOutsideTrainees(outsideTrainees.map(t =>
      t.id === traineeId ? { ...t, competency_status: status } : t
    ));
  };

  // Add all users to trainees
  const handleAddAllUsers = () => {
    const allUserIds = combinedTrainees.map(user => user.id);
    form.setFieldsValue({
      asset: {
        ...form.getFieldValue('asset'),
        trainee_ids: allUserIds
      }
    });
    message.success(`Added ${allUserIds.length} users to trainees`);
  };
  const getLatLng = (cLat, cLng) => {
    const lat = cLat;
    const lng = cLng;
    return new L.LatLng(
          lat,
          lng
    );
  }

  const uploadPolicyProps = {
    name: "Attachment[file]",
    action: apiEndpoint.attachments,
    headers: authHeader(true),
    listType: "picture",
    multiple: true,
    onChange: (response) => {
      if(response.file && response.file.status == "done"){
        setAttachmentActivityId(attachmentActivityId => [...attachmentActivityId, response.file.response.attachment.id]);
      }
    },
    onRemove: file => {
      attachment.destroy(file.uid);
    }

  };

  const renderPropertyUsers = (placeholder = "Select user", mode = undefined) => {
    return (
      <Select
        placeholder={placeholder}
        allowClear
        size="large"
        mode={mode}
        showSearch
        optionFilterProp="label"
      >
        {propertyUsers.map((user) => (
          <Option key={user.id} value={user.id} label={`${user.name} (${user.email})`}>
            {user.name} ({user.email})
          </Option>
        ))}
      </Select>
    );
  }

  const renderTrainees = (placeholder = "Select trainees", mode = "multiple") => {
    return (
      <Select
        placeholder={placeholder}
        allowClear
        size="large"
        mode={mode}
        showSearch
        optionFilterProp="label"
        loading={traineesLoading}
      >
        {combinedTrainees.map((user) => (
          <Option key={user.id} value={user.id} label={`${user.name} (${user.email})`}>
            {user.name} ({user.email})
          </Option>
        ))}
      </Select>
    );
  }

  const handleClickMap = (e) => {
     const { lat, lng } = e.latlng;
     setValueLng(lng);
     setValuelat(lat);
  };
  const renderActivityPolicy = () => {
    let options = [];
    _.forEach(activityPolicy , (activity, index) => {
      options.push(
        <Option key={index} value={activity.value}>
          <div>{activity.label}</div>
        </Option>
      );
    })
    return (
      <Select
        placeholder="Select a activity"
        allowClear
        size="large"
      >
        {options}
      </Select>
    );
  } 
  const handleSubmit = (e) => {
    setLoading(true);

    form
      .validateFields()
      .then(values => {
        onStore(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
        setLoading(false);
      });
  }
  const onStore = (values) => {
    var point = null;
    if(values.asset.type === 'comment'){
      if(valueLat !== '' && valueLng !== ''){
        point = getLatLng(valueLat,valueLng);
      }
    }

    // For training_session, extract trainee names from IDs
    let trainee_names = [];
    if(values.asset.type === 'training_session' && values.asset.trainee_ids){
      trainee_names = values.asset.trainee_ids.map(traineeId => {
        const user = combinedTrainees.find(u => u.id === traineeId);
        return user ? user.name : `User ${traineeId}`;
      });
    }

    // Format training session dates and times
    let sessionData = {};
    if(values.asset.type === 'training_session'){
      // The dropdown uses sentinels for external trainers (primary_external /
      // secondary_external). Translate to NULL trainer_id and flag which
      // external slot was picked so the backend can resolve the name from
      // the asset on read.
      const rawTrainer = values.asset.trainer_id;
      const externalTrainerSlot = rawTrainer === 'primary_external'
        ? 'primary'
        : rawTrainer === 'secondary_external'
        ? 'secondary'
        : null;
      const trainerIdValue = externalTrainerSlot ? null : rawTrainer;
      sessionData = {
        training_name: values.asset.training_name,
        // DB column is TIME (HH:mm:ss). Send time-of-day, matching RunSessionWizard.
        start_time: values.asset.start_time ? moment(values.asset.start_time).format('HH:mm:ss') : '',
        finish_time: values.asset.finish_time ? moment(values.asset.finish_time).format('HH:mm:ss') : '',
        trainer_id: trainerIdValue,
        external_trainer_slot: externalTrainerSlot,
        delivery_methods: values.asset.delivery_methods,
        division_id: values.asset.division_id,
        site_ids: values.asset.site_ids,
        // Multiple outside trainees
        outside_trainees: outsideTrainees.map(t => ({
          first_name: t.first_name,
          last_name: t.last_name,
          email: t.email,
          mobile_phone: t.mobile_phone,
          competency_status: t.competency_status,
        })),
        location_name: values.asset.location_name,
        equipment: values.asset.equipment,
      };
    }

    // Build policy data - only include relevant fields for each type
    const policyData = {
      idFacilities: props.dataId,
      property_id: props.propertyId ?? null,
      type: values.asset.type,
      date_time: values.asset.date_time ? dateHelper.dateTimeForAPI(values.asset.date_time) : '',
      attachment_activity_id: attachmentActivityId,
    };

    // Add type-specific fields
    if (values.asset.type === 'training_session') {
      // Only add training session specific fields from sessionData
      Object.assign(policyData, sessionData);
      policyData.trainee_names = trainee_names.length > 0 ? trainee_names : undefined;
      policyData.trainee_ids = values.asset.trainee_ids;

      // Fill default "competent" for trainees without explicit competency status
      const finalCompetencies = { ...traineeCompetencies };
      if (values.asset.trainee_ids && Array.isArray(values.asset.trainee_ids)) {
        values.asset.trainee_ids.forEach(traineeId => {
          if (!finalCompetencies[traineeId]) {
            finalCompetencies[traineeId] = 'competent';
          }
        });
      }
      policyData.trainee_competencies = finalCompetencies;
      policyData.notes = values.asset.notes;
    } else {
      // For other types (comment, meeting, update), include all asset fields and point
      Object.assign(policyData, values.asset);
      policyData.point = point;
      policyData.meeting_attendees = attendees;
    }

    const dataPolicy = {
      policy: policyData,
    };
    healthsafety.storeOrUpdateActivityPolicies(dataPolicy).then(res => {
      if(res.code === '0000'){
        setLoading(false);
        message.success('Training activity recorded successfully');
        props.cancelModalActivity();
        form.resetFields();
        props.getDataActivity(props.dataId);
        setAttendees([]);
      } else {
        setLoading(false);
        if (res.message) {
          message.error(res.message);
        } else {
          message.error('Failed to save activity. Please try again.');
        }
      }
    });
  }


  return(
    <>
    <AssetModalWrapper
      visible={props.visibleActivity}
      onCancel={props.cancelModalActivity}
      title= "Add Activity"
      footer={[
        <Button key="back" onClick={props.cancelModalActivity} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
        <Button key="submit" className="btn-success" type="primary" onClick={handleSubmit} loading={loading}>
          {<IntlMessages id="propertyPage.modal.save" />}
        </Button>,
      ]}
    >
    <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        fields={fields}
        id="#1"
      >
      <div className="form-body">
      <Row>
        <Col xs={{span: 24}} md={{span: 24}}>
            <Form.Item
              name={['asset','type']}
              className="isoInputWrapper"
              label={<IntlMessages id="propertyPage.modal.asset.activityPolicy" />}
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
            >
              {renderActivityPolicy()}
            </Form.Item>
          </Col>
      </Row>
       <Row>
          <Col xs={{span: 24}} md={{span: 24}}>
            <Form.Item
              name={['asset', 'date_time']}
              className="isoInputWrapper"
              label="Date Time"
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
            >
              <DatePicker format="DD-MM-YYYY HH:mm:ss" size="large" style={{width: '100%'}} showTime getPopupContainer={(trigger) => trigger.parentNode}/>
            </Form.Item>
          </Col>
      </Row>
      <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => {
            return ( prevValues['asset'] );
          }}
        >
        {({ getFieldValue }) => {
          if(getFieldValue('asset')){
             
            return (
              <div>
                { getFieldValue('asset').type === 'meeting' &&
                  <div>
                    <Row>
                      <Col xs={{span: 24}} md={{span: 11}}>
                        <Form.Item
                          name={['asset', 'meeting_name']}
                          className="isoInputWrapper"
                          label="Meeting Name"
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Input size="large" placeholder="Meeting Name"/>
                        </Form.Item>
                      </Col>
                      <Col xs={{span: 24}} md={{span: 11,offset:2}}>
                        <Form.Item
                          name={['asset', 'meeting_actions']}
                          className="isoInputWrapper"
                          label="Meeting Actions"
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Input size="large" placeholder="Meeting Actions"/>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={{span: 24}} md={{span: 24}}>
                        <Form.Item
                          className="isoInputWrapper"
                          label="Meeting Attendees"
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <TagsInput name={['asset', 'meeting_attendees']} value={attendees} onChange={handleChangeTag} addOnBlur="true"/>

                        </Form.Item>
                      </Col>
                      <Col xs={{span: 24}} md={{span: 24}}>
                        <Form.Item
                          name={['asset', 'meeting_notes']}
                          className="isoInputWrapper"
                          label="Meeting Notes"
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Input.TextArea />
                        </Form.Item>
                      </Col>
                    </Row>

                  </div>
                }
                { (getFieldValue('asset').type === 'comment' || getFieldValue('asset').type === 'update') &&
                        <div>
                          <Row>
                           <Col xs={{span: 24}} md={{span: 24}} >
                            <Form.Item
                              name={['asset', 'comments']}
                              className="isoInputWrapper"
                              label="Comment"
                            >
                              <Input.TextArea />
                            </Form.Item>
                            </Col>
                          </Row>
                            <Row>
                              <Col xs={{span: 24}} md={{span: 24}} >
                                <Form.Item
                                  name={['asset', 'attachment']}
                                  className="isoInputWrapper"
                                  label={<IntlMessages id="propertyPage.modal.asset.attachmentPolicy" />}
                                >
                                  <Upload {...uploadPolicyProps}>
                                    <Button icon={<UploadOutlined />}>Upload</Button>
                                  </Upload>
                                
                                </Form.Item>
                              </Col>
                            </Row>

                        </div>
                      }
                      {getFieldValue('asset').type == 'comment' &&
                        <div>
                          <Map ref={mapRef} center={props.position[0] !== null ? props.position : false} doubleClickZoom={false} onclick={handleClickMap} zoom={16} zoomControl={true} style={{width:'100%', height:'300px'}}>
                             <TileLayer
                              attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              />
                              {valueLat  && <Marker position={[valueLat,valueLng]} draggable={true}>
                                  <Popup position={[valueLat,valueLng]}>
                                  </Popup>
                              </Marker>}
                          </Map>
                          <Row style={{marginTop:"10px", marginBottom:"5px"}}>
                              <Col xs={{span: 24}} md={{span: 11}} >
                                  <Input value={valueLat}/>
                              </Col>
                              <Col xs={{span: 24}} md={{span: 11, offset: 2}} >
                                  <Input value={valueLng}/>
                              </Col>
                          </Row>
                        </div>
                      }
                      { getFieldValue('asset').type === 'training_session' &&
                        <div>
                          <Row>
                            <Col xs={{span: 24}} md={{span: 24}}>
                              <Form.Item
                                name={['asset', 'training_name']}
                                label="Training/Course Name"
                                rules={[
                                  { required: true, whitespace: true, message: 'Training name is required' }
                                ]}
                              >
                                <Input size="large" placeholder="Enter training/course name"/>
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={16}>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'start_time']}
                                label="Start Time"
                                rules={[
                                  { required: true, message: 'Start time is required' }
                                ]}
                              >
                                <TimePicker
                                  format="HH:mm"
                                  style={{ width: '100%' }}
                                  size="large"
                                  getPopupContainer={(trigger) => trigger.parentNode}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'finish_time']}
                                label="Finish Time"
                                dependencies={[['asset', 'start_time']]}
                                rules={[
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      const startTime = getFieldValue(['asset', 'start_time']);
                                      if (!value || !startTime) {
                                        return Promise.resolve();
                                      }
                                      if (value.isBefore(startTime)) {
                                        return Promise.reject(new Error('Finish time cannot be before start time'));
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <TimePicker
                                  format="HH:mm"
                                  style={{ width: '100%' }}
                                  size="large"
                                  getPopupContainer={(trigger) => trigger.parentNode}
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={16}>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'trainer_id']}
                                label="Trainer"
                                rules={[
                                  { required: true, message: 'Trainer is required' }
                                ]}
                              >
                                <Select
                                  placeholder="Select trainer"
                                  allowClear
                                  size="large"
                                  showSearch
                                  filterOption={(input, option) =>
                                    String(option.children).toLowerCase().includes(input.toLowerCase())
                                  }
                                >
                                  {primaryExternal && (
                                    <Option key="primary_external" value="primary_external" style={{ fontStyle: 'italic' }}>
                                      {primaryExternal.name} (External — Primary)
                                    </Option>
                                  )}
                                  {secondaryExternal && (
                                    <Option key="secondary_external" value="secondary_external" style={{ fontStyle: 'italic' }}>
                                      {secondaryExternal.name} (External — Secondary)
                                    </Option>
                                  )}
                                  {trainerOptions.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                      {user.name}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'delivery_methods']}
                                label="Delivery Methods"
                                rules={[
                                  { required: true, message: 'At least one delivery method is required' }
                                ]}
                              >
                                <Select
                                  mode="multiple"
                                  placeholder="Select delivery methods"
                                  allowClear
                                  size="large"
                                  optionFilterProp="label"
                                >
                                  {deliveryMethods.map((method) => (
                                    <Option key={method.value} value={method.value} label={method.label}>
                                      {method.label}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={16}>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'division_id']}
                                label="Division"
                              >
                                <Select
                                  placeholder="Select division"
                                  allowClear
                                  size="large"
                                  showSearch
                                  optionFilterProp="label"
                                  onChange={(value) => {
                                    setSelectedDivision(value);
                                    // Clear site_ids and trainee_ids when division changes
                                    form.setFieldsValue({
                                      asset: {
                                        ...form.getFieldValue('asset'),
                                        site_ids: [],
                                        trainee_ids: []
                                      }
                                    });
                                    // Reset trainees to current property users
                                    setCombinedTrainees(propertyUsers);
                                  }}
                                >
                                  {divisions.map((division) => (
                                    <Option key={division.id} value={division.id} label={division.name}>
                                      {division.name}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'site_ids']}
                                label="Sites (Property Addresses)"
                              >
                                <Select
                                  mode="multiple"
                                  placeholder={selectedDivision ? "Select property addresses" : "Please select a division first"}
                                  allowClear
                                  size="large"
                                  showSearch
                                  optionFilterProp="label"
                                  disabled={!selectedDivision}
                                  onChange={(selectedSiteIds) => {
                                    loadTraineesFromSites(selectedSiteIds);
                                  }}
                                >
                                  {sites.map((site) => (
                                    <Option key={site.id} value={site.id} label={`${site.property_name} - ${site.property_address}`}>
                                      {site.property_name} - {site.property_address}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={{span: 24}} md={{span: 24}}>
                              <Form.Item
                                name={['asset', 'trainee_ids']}
                                label={
                                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <span>Trainees</span>
                                    <Button
                                      type="link"
                                      icon={<UsergroupAddOutlined />}
                                      onClick={handleAddAllUsers}
                                      style={{ padding: 0, height: 'auto', marginLeft: 12, color: '#0b2a1b' }}
                                    >
                                      Add All Users
                                    </Button>
                                  </div>
                                }
                                rules={[{ required: outsideTrainees.length === 0, message: 'At least one trainee is required' }]}
                              >
                                {renderTrainees("Select trainees", "multiple")}
                              </Form.Item>
                            </Col>
                          </Row>

                          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
                            prevValues.asset?.trainee_ids !== currentValues.asset?.trainee_ids
                          }>
                            {({ getFieldValue }) => {
                              const selectedTraineeIds = getFieldValue(['asset', 'trainee_ids']) || [];

                              if (selectedTraineeIds.length === 0) {
                                return null;
                              }

                              return (
                                <div style={{ marginBottom: 24 }}>
                                  <h4>Competency Assessment</h4>
                                  {selectedTraineeIds.map(traineeId => {
                                    const user = combinedTrainees.find(u => u.id === traineeId);
                                    if (!user) return null;

                                    return (
                                      <Card
                                        key={traineeId}
                                        size="small"
                                        style={{ marginBottom: 12 }}
                                      >
                                        <Row gutter={16} align="middle">
                                          <Col span={12}>
                                            <strong>{user.name}</strong>
                                          </Col>
                                          <Col span={12}>
                                            <Radio.Group
                                              value={traineeCompetencies[traineeId] || 'competent'}
                                              onChange={(e) => {
                                                setTraineeCompetencies({
                                                  ...traineeCompetencies,
                                                  [traineeId]: e.target.value
                                                });
                                              }}
                                            >
                                              <Radio value="competent">Competent</Radio>
                                              <Radio value="not_yet_competent">Not Yet Competent</Radio>
                                            </Radio.Group>
                                          </Col>
                                        </Row>
                                      </Card>
                                    );
                                  })}
                                </div>
                              );
                            }}
                          </Form.Item>

                          <Divider orientation="left">Outside Trainees</Divider>

                          {/* Table of added outside trainees */}
                          {outsideTrainees.length > 0 && (
                            <Table
                              dataSource={outsideTrainees}
                              rowKey="id"
                              size="small"
                              pagination={false}
                              style={{ marginBottom: 16 }}
                              scroll={{ x: 'max-content' }}
                              expandable={{
                                expandedRowRender: (record) => (
                                  <div style={{ padding: '8px 0' }}>
                                    {record.email && (
                                      <p style={{ margin: '4px 0' }}>
                                        <strong>Email:</strong> {record.email}
                                      </p>
                                    )}
                                    {record.mobile_phone && (
                                      <p style={{ margin: '4px 0' }}>
                                        <strong>Mobile:</strong> {record.mobile_phone}
                                      </p>
                                    )}
                                    <div style={{ marginTop: 8 }}>
                                      <strong>Competency:</strong>
                                      <Radio.Group
                                        value={record.competency_status}
                                        onChange={(e) => handleOutsideTraineeCompetencyChange(record.id, e.target.value)}
                                        size="small"
                                        style={{ marginLeft: 8 }}
                                      >
                                        <Radio value="competent">Competent</Radio>
                                        <Radio value="not_yet_competent">Not Yet Competent</Radio>
                                      </Radio.Group>
                                    </div>
                                  </div>
                                ),
                                rowExpandable: () => true,
                                columnWidth: 40,
                              }}
                              columns={[
                                {
                                  title: 'Name',
                                  key: 'name',
                                  render: (_, record) => `${record.first_name} ${record.last_name}`.trim(),
                                },
                                {
                                  title: 'Email',
                                  dataIndex: 'email',
                                  key: 'email',
                                  responsive: ['lg'],
                                },
                                {
                                  title: 'Mobile',
                                  dataIndex: 'mobile_phone',
                                  key: 'mobile_phone',
                                  responsive: ['lg'],
                                },
                                {
                                  title: 'Competency',
                                  key: 'competency',
                                  render: (_, record) => (
                                    <Radio.Group
                                      value={record.competency_status}
                                      onChange={(e) => handleOutsideTraineeCompetencyChange(record.id, e.target.value)}
                                      size="small"
                                    >
                                      <Radio value="competent">Competent</Radio>
                                      <Radio value="not_yet_competent">Not Yet Competent</Radio>
                                    </Radio.Group>
                                  ),
                                  responsive: ['md'],
                                },
                                {
                                  title: '',
                                  key: 'action',
                                  width: 40,
                                  fixed: 'right',
                                  render: (_, record) => (
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => handleRemoveOutsideTrainee(record.id)}
                                      style={{ marginRight: -1 }}
                                    />
                                  ),
                                },
                              ]}
                            />
                          )}

                          {/* Form to add new outside trainee */}
                          <Card size="small" title="Add Outside Trainee" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                              <Col xs={{span: 24}} md={{span: 12}}>
                                <Form.Item
                                  name={['asset', 'outside_trainee_first_name']}
                                  label="First Name"
                                  style={{ marginBottom: 8 }}
                                >
                                  <Input size="large" placeholder="First name"/>
                                </Form.Item>
                              </Col>
                              <Col xs={{span: 24}} md={{span: 12}}>
                                <Form.Item
                                  name={['asset', 'outside_trainee_last_name']}
                                  label="Last Name"
                                  style={{ marginBottom: 8 }}
                                >
                                  <Input size="large" placeholder="Last name"/>
                                </Form.Item>
                              </Col>
                            </Row>

                            <Row gutter={16}>
                              <Col xs={{span: 24}} md={{span: 12}}>
                                <Form.Item
                                  name={['asset', 'outside_trainee_email']}
                                  label="Email"
                                  rules={[
                                    { type: 'email', message: 'Please enter a valid email' }
                                  ]}
                                  style={{ marginBottom: 8 }}
                                >
                                  <Input size="large" placeholder="email@example.com"/>
                                </Form.Item>
                              </Col>
                              <Col xs={{span: 24}} md={{span: 12}}>
                                <Form.Item
                                  name={['asset', 'outside_trainee_mobile']}
                                  label="Mobile Phone"
                                  style={{ marginBottom: 8 }}
                                >
                                  <Input size="large" placeholder="Mobile phone number"/>
                                </Form.Item>
                              </Col>
                            </Row>

                            <Button
                              type="dashed"
                              icon={<PlusOutlined />}
                              onClick={handleAddOutsideTrainee}
                              style={{ width: '100%' }}
                              className="btn-add-outside-trainee"
                            >
                              Add Outside Trainee
                            </Button>
                          </Card>

                          <Row>
                            <Col xs={{span: 24}} md={{span: 24}}>
                              <Form.Item
                                name={['asset', 'location_name']}
                                label="Location"
                              >
                                <Input size="large" placeholder="Where was the training held?"/>
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={{span: 24}} md={{span: 24}}>
                              <Form.Item
                                name={['asset', 'equipment']}
                                label="Equipment Used"
                              >
                                <Input.TextArea rows={2} placeholder="Equipment used during training"/>
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={{span: 24}} md={{span: 24}}>
                              <Form.Item
                                name={['asset', 'notes']}
                                label="Notes"
                              >
                                <Input.TextArea rows={4} placeholder="Additional training details"/>
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      }

              </div>
            )
          }
        }}

      </Form.Item>
     
      </div>
      </Form>
    </AssetModalWrapper>
    </>
  )

}

export default AddActivityModal;
